import React, { createContext, useContext, useMemo, useState, useEffect } from 'react';

const CartContext = createContext(null);
const STORAGE_KEY = 'vemmac_kitchen_cart';

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState(() => {
    if (typeof window === 'undefined') return [];
    try {
      const stored = window.localStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch (err) {
      console.warn('Failed to load cart from localStorage:', err);
      return [];
    }
  });

  // Sync cart to localStorage whenever it changes
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(cartItems));
    } catch (err) {
      console.warn('Failed to save cart to localStorage:', err);
    }
  }, [cartItems]);

  const addItem = (item, quantity = 1) => {
    setCartItems((prev) => {
      const existingIndex = prev.findIndex(
        (cartItem) => cartItem.id === item.id && cartItem.type === item.type
      );

      if (existingIndex > -1) {
        const updated = [...prev];
        updated[existingIndex] = {
          ...updated[existingIndex],
          quantity: updated[existingIndex].quantity + quantity,
        };
        return updated;
      }

      return [...prev, { ...item, quantity }];
    });
  };

  const updateItem = (itemId, type, quantity) => {
    setCartItems((prev) =>
      prev.map((item) =>
        item.id === itemId && item.type === type
          ? { ...item, quantity: Math.max(1, quantity) }
          : item
      )
    );
  };

  const patchItem = (itemId, type, updater) => {
    setCartItems((prev) =>
      prev.map((item) => {
        if (!(item.id === itemId && item.type === type)) return item;
        if (typeof updater === 'function') {
          const patched = updater(item);
          return patched ? { ...item, ...patched } : item;
        }
        return { ...item, ...updater };
      })
    );
  };

  const removeItem = (itemId, type) => {
    setCartItems((prev) =>
      prev.filter((item) => !(item.id === itemId && item.type === type))
    );
  };

  const clearCart = () => setCartItems([]);

  const totalQuantity = useMemo(
    () => cartItems.reduce((sum, item) => sum + item.quantity, 0),
    [cartItems]
  );

  const totalAmount = useMemo(
    () => cartItems.reduce((sum, item) => sum + item.price * item.quantity, 0),
    [cartItems]
  );

  const hasItems = cartItems.length > 0;

  return (
    <CartContext.Provider
      value={{
        cartItems,
        addItem,
        updateItem,
        patchItem,
        removeItem,
        clearCart,
        totalQuantity,
        totalAmount,
        hasItems,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};